'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { TimeRangeConfig } from '@/api/generated/model';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { buildAiSpendGroupAggregatesPayload, buildAiSpendOrgAggregatesPayload, buildAiSpendSegmentTrendPayload, buildAiSpendTrendAggregatesPayload } from '@/domains/pages/ai-spend/utils/ai-spend-api-payloads';
import type { AiSpendOrgSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { mapOrgSpendSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { mapSpendTrendSeries, type SpendTrendSeries } from '@/domains/pages/ai-spend/utils/map-ai-spend-trend';
import { applySegmentSpendDisplayNames, computeSegmentSpendPerTaskRank, computeSpendPerTaskRange, getSegmentSpendDisplayName, mapSegmentSpendSummaries, type SegmentSpendSummary, type SpendPerTaskRange } from '@/domains/pages/ai-spend/utils/map-ai-spend-segments';
import { buildSpendPeerBand, mapSegmentSpendTrend, type SpendPeerBandPoint } from '@/domains/pages/ai-spend/utils/map-ai-spend-segment-trend';
import { NEBULY_PAGE_QUERY_OPTIONS, getPageTimeRangeQueryKey } from '@/domains/pages/shared/page-query-options';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import type { SegmentKind } from '@/domains/pages/shared/segment-kind';
import { getTrendQuery } from '@/domains/pages/shared/trend-query';
import { useNebulyPageEffectiveNow } from '@/domains/pages/hooks/use-nebuly-page-effective-now';
import { useNebulyPageStableNow } from '@/domains/pages/hooks/use-nebuly-page-stable-now';
import { getTimeRangeMemoKey } from '@/domains/pages/utils/nebuly-page-time-range';
import { useCallback, useMemo } from 'react';
const EMPTY_SPEND_TREND: SpendTrendSeries = {
  labels: [],
  totalSpend: [],
  spendPerTask: []
};
const EMPTY_ORG: AiSpendOrgSnapshot = {
  totalCost: null,
  totalCostPrev: null,
  tasks: 0,
  spendPerTask: null
};
export type AiSpendSegmentPageData = {
  segments: SegmentSpendSummary[];
  selectedSegmentName: string;
  selected: SegmentSpendSummary | null;
  org: AiSpendOrgSnapshot;
  spendPerTaskRank: number;
  spendPerTaskRange: SpendPerTaskRange;
  labels: string[];
  totalSpend: Array<number | null>;
  spendPerTask: Array<number | null>;
  orgSpendPerTaskOverTime: Array<number | null>;
  peerBand: SpendPeerBandPoint[];
  formatSegmentName: (name: string) => string;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};
type UseAiSpendSegmentPageDataParams = {
  projectId: string | undefined;
  timeRange: TimeRangeConfig | undefined;
  kind: SegmentKind;
  selectedSegmentName: string | undefined;
  filters?: GenericFilter[];
  specialFilters?: GenericFilter[];
  enabled?: boolean;
};
export function useAiSpendSegmentPageData({
  projectId,
  timeRange,
  kind,
  selectedSegmentName,
  filters = [],
  specialFilters = [],
  enabled: enabledParam = true
}: UseAiSpendSegmentPageDataParams): AiSpendSegmentPageData {
  const stableNow = useNebulyPageStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useNebulyPageEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const timeRangeMemoKey = getTimeRangeMemoKey(timeRange);
  const resolvedTimeRange = useMemo(() => resolveNebulyPageTimeRange(timeRange, stableNow, effectiveNow), [timeRangeMemoKey, stableNowKey, effectiveNowKey]);
  const trendQuery = useMemo(() => getTrendQuery(resolvedTimeRange, stableNow, effectiveNow), [timeRangeMemoKey, resolvedTimeRange, stableNowKey, effectiveNowKey]);
  const timeRangeQueryKey = useMemo(() => getPageTimeRangeQueryKey(resolvedTimeRange), [resolvedTimeRange]);
  const trendQueryKey = useMemo(() => ({
    granularity: trendQuery.granularity,
    bucketKind: trendQuery.bucketKind,
    timeRange: getPageTimeRangeQueryKey(trendQuery.time_range)
  }), [trendQuery]);
  const filtersQueryKey = useMemo(() => JSON.stringify(filters), [filters]);
  const specialFiltersQueryKey = useMemo(() => JSON.stringify(specialFilters), [specialFilters]);
  const enabled = enabledParam && Boolean(projectId && resolvedTimeRange && isEffectiveNowReady);
  const orgQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-org', projectId, timeRangeQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal
    }) => getInteractionAggregates(buildAiSpendOrgAggregatesPayload({
      projectId: projectId!,
      timeRange: resolvedTimeRange!,
      filters,
      specialFilters
    }), {
      signal
    }),
    enabled,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const orgReady = enabled && orgQuery.isSuccess;
  const peersQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-segments', kind, projectId, timeRangeQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal: signal_0
    }) => getInteractionAggregates(buildAiSpendGroupAggregatesPayload({
      projectId: projectId!,
      timeRange: resolvedTimeRange!,
      filters,
      specialFilters,
      kind
    }), {
      signal: signal_0
    }),
    enabled: orgReady,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const segments = useMemo(() => {
    const raw = peersQuery.data ? mapSegmentSpendSummaries(peersQuery.data) : [];
    return applySegmentSpendDisplayNames(raw, kind);
  }, [kind, peersQuery.data]);
  const formatSegmentName = useCallback((name: string) => getSegmentSpendDisplayName(name, segments), [segments]);
  const resolvedSelectedName = useMemo(() => {
    if (selectedSegmentName && segments.some(segment => segment.name === selectedSegmentName)) {
      return selectedSegmentName;
    }
    return segments[0]?.name ?? '';
  }, [segments, selectedSegmentName]);
  const segmentReady = orgReady && peersQuery.isSuccess && Boolean(resolvedSelectedName);
  const orgTrendQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-trend', projectId, trendQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal: signal_1
    }) => getInteractionAggregates(buildAiSpendTrendAggregatesPayload({
      projectId: projectId!,
      trendQuery,
      filters,
      specialFilters
    }), {
      signal: signal_1
    }),
    enabled: orgReady,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const segmentTrendQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-segment-trend', kind, projectId, trendQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal: signal_2
    }) => getInteractionAggregates(buildAiSpendSegmentTrendPayload({
      projectId: projectId!,
      trendQuery,
      filters,
      specialFilters,
      kind
    }), {
      signal: signal_2
    }),
    enabled: segmentReady,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const org = useMemo(() => orgQuery.data ? mapOrgSpendSnapshot(orgQuery.data) : EMPTY_ORG, [orgQuery.data]);
  const selected = useMemo(() => segments.find(segment_0 => segment_0.name === resolvedSelectedName) ?? null, [resolvedSelectedName, segments]);
  const spendPerTaskRank = useMemo(() => computeSegmentSpendPerTaskRank(segments, resolvedSelectedName), [resolvedSelectedName, segments]);
  const spendPerTaskRange = useMemo(() => computeSpendPerTaskRange(segments), [segments]);
  const orgSpendTrend = useMemo(() => orgTrendQuery.data ? mapSpendTrendSeries(orgTrendQuery.data, trendQuery) : EMPTY_SPEND_TREND, [orgTrendQuery.data, trendQuery]);
  const segmentTrend = useMemo(() => {
    if (!segmentTrendQuery.data) {
      return {
        labels: [] as string[],
        totalSpendBySegment: {} as Record<string, Array<number | null>>,
        spendPerTaskBySegment: {} as Record<string, Array<number | null>>
      };
    }
    return mapSegmentSpendTrend(segmentTrendQuery.data, trendQuery);
  }, [segmentTrendQuery.data, trendQuery]);
  const totalSpend = segmentTrend.totalSpendBySegment[resolvedSelectedName] ?? [];
  const spendPerTask = segmentTrend.spendPerTaskBySegment[resolvedSelectedName] ?? [];
  const orgSpendPerTaskOverTime = orgSpendTrend.spendPerTask;
  const peerBand = useMemo(() => buildSpendPeerBand(segmentTrend.spendPerTaskBySegment, segmentTrend.labels.length), [segmentTrend.labels.length, segmentTrend.spendPerTaskBySegment]);
  const isLoading = orgQuery.isLoading || orgReady && peersQuery.isLoading || orgReady && orgTrendQuery.isLoading || segmentReady && segmentTrendQuery.isLoading;
  const isError = orgQuery.isError || peersQuery.isError || orgTrendQuery.isError || segmentTrendQuery.isError;
  const error = orgQuery.error ?? peersQuery.error ?? orgTrendQuery.error ?? segmentTrendQuery.error;
  const refetch = useCallback(() => {
    void orgQuery.refetch();
    void peersQuery.refetch();
    void orgTrendQuery.refetch();
    void segmentTrendQuery.refetch();
  }, [orgQuery.refetch, orgTrendQuery.refetch, peersQuery.refetch, segmentTrendQuery.refetch]);
  return {
    segments,
    selectedSegmentName: resolvedSelectedName,
    selected,
    org,
    spendPerTaskRank,
    spendPerTaskRange,
    labels: segmentTrend.labels,
    totalSpend,
    spendPerTask,
    orgSpendPerTaskOverTime,
    peerBand,
    formatSegmentName,
    isLoading,
    isError,
    error,
    refetch
  };
}